(ns app.front.view.permit.conditions
  (:require [app.domain.application.condition.model :as condition-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui.dnd :as dnd]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.conditions.common :as condition-common]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-sub ::verdict-condition-ids
  (fn [[_ section-id]]
    (rf/subscribe [::entities/entity section-id]))
  (fn [section]
    (->> section
         :verdict.section/entities
         (sort-by :ordered/order)
         (map :db/id))))

(rf/reg-sub ::phase-condition-ids
  (fn [[_ fsm-target-id]]
    (rf/subscribe [::condition-common/permit-specific-conditions fsm-target-id]))
  (fn [conditions]
    (->> conditions
         (map :db/id)
         seq)))

(rf/reg-sub ::condition-fields
  (fn [[_ id]]
    (rf/subscribe [::entities/entity id]))
  (fn [entity _]
    (-> entity
        (select-keys (conj condition-model/condition-required-keys
                           :application.condition/due-date)))))

(rf/reg-sub ::condition-state
  (fn [[_ id]]
    (rf/subscribe [::entities/entity id]))
  :-> condition-model/condition->state)

(defn- add-general-condition-controls
  [{:keys [tr group-id application-id verdict-section-id]}]
  (r/with-let [*state (r/atom {})]
    (let [{:keys [adding? value]} (deref *state)
          templates               (<sub [::condition-common/templates-for-group group-id])]
      (when (seq templates)
        (if-not adding?
          (cc/button {:kind     :primary
                      :icon     i/plus
                      :size     :small
                      :on-click #(swap! *state assoc :adding? true)}
                     (tr [:condition.template/add]))
          [:div.flex.max-w-xl
           [ui-select/select
            {:values       templates
             :val->str     :application.condition.template/title
             :id           "add-condition-template-selector"
             :required     true
             :class        "flex-shrink"
             :allow-empty? false
             :value        value
             :on-change    #(swap! *state assoc :value %)}]
           (cc/button {:kind             :ghost
                       :icon             i/remove
                       :size             :field
                       :icon-description "remove"
                       :class-name       "only-icon-button"
                       :on-click         #(reset! *state {})}
                      "")
           (cc/button {:kind             :ghost
                       :icon             i/check
                       :icon-description "add"
                       :size             :field
                       :disabled         (nil? value)
                       :class-name       "only-icon-button"
                       :on-click         #(do
                                            (>evt [::process/signal
                                                   application-id
                                                   [:process.signal/add-general-condition-to-section
                                                    (util/mmap {:template-id (:db/id value)}
                                                               verdict-section-id)]
                                                   {:notify {:success :changes-saved}}])
                                            (reset! *state {}))}
                      "")])))))

(defn- condition-row
  [{:keys [*item-state tr id fsm-target-id group-id index reordering?
           edit-signal delete-signal can-reorder? can-edit? can-delete?
           augment-target-props augment-draggable-props]}]
  (let [entity-fields             (<sub [::condition-fields id])
        condition-entity          (<sub [::entities/entity id])
        {:keys [editing? fields]} (-> *item-state deref)
        current-fields            (merge entity-fields fields)
        modified?                 (not= current-fields entity-fields)]
    (if editing?
      [condition-common/condition-editor-view
       (util/mmap {:default-group-id    group-id
                   :can-save?           (and can-edit?
                                             (condition-model/valid-condition? current-fields)
                                             modified?)
                   :save-tr-key         :condition/save
                   :get-field           (fn [_ field] (get current-fields field))
                   :set-field           (fn [_ field val] (swap! *item-state update :fields assoc field val))
                   :on-save             #(do
                                           (>evt [::process/signal
                                                  fsm-target-id
                                                  [edit-signal (assoc fields :id id)]
                                                  {:notify {:success :changes-saved}}])
                                           (swap! *item-state assoc :editing? false))
                   :on-cancel           #(reset! *item-state {})
                   :on-delete           #(>evt [::process/signal
                                                fsm-target-id
                                                [delete-signal {:id id}]
                                                {:notify {:success :condition/deleted}}])
                   :condition-instance? true}
                  id tr can-delete?)]
      (let [state (<sub [::condition-state id])]
        [condition-common/condition-row
         (util/mmap {:item             id
                     :item->state      (constantly state)
                     :item->body       (fn [_] (get current-fields :application.condition/body))
                     :item->title      (fn [_] (get current-fields :application.condition/title))
                     :item->due-date   (fn [_] (get current-fields :application.condition/due-date))
                     ;; addressed-to has been deprecated, this might not be needed in this permit context anymore
                     :item->group-name #(-> condition-entity :application.condition/addressed-to :group/name)
                     :border-color     (-> state condition-common/state->border)
                     :on-edit          (fn [_] (swap! *item-state assoc :editing? true))}
                    id index tr reordering? can-edit? can-reorder? augment-target-props augment-draggable-props)]))))

(defn- reorder-buttons
  [{:keys [tr *reordering]}]
  (if (deref *reordering)
    (cc/button {:kind     :primary
                :icon     i/remove
                :size     :small
                :on-click #(reset! *reordering false)}
               (tr [:done]))
    (cc/button {:kind     :tertiary
                :icon     i/arrows-up-down
                :size     :small
                :on-click #(reset! *reordering true)}
               (tr [:condition.template/reorder]))))

(defn- condition-list
  [{:keys [tr ids fsm-target-id group-id title editable? edit-signal delete-signal reorder-signal]}
   & other-controls]
  (r/with-let [*state (r/atom {:reordering? false
                               :items       {}})]
    (let [*reordering  (r/cursor *state [:reordering?])
          reordering?  (-> *reordering deref)
          can-edit?    (and editable? (<sub [::process/signal-permitted? fsm-target-id [edit-signal]]))
          can-delete?  (and editable? (<sub [::process/signal-permitted? fsm-target-id [delete-signal]]))
          can-reorder? (and editable? (<sub [::process/signal-permitted? fsm-target-id [reorder-signal]]))]
      [:div.verdict-page__section
       [:h4 (tr [title])]
       [:div.mb-6
        [condition-common/header-row (util/mmap {:title     (tr [title])
                                                 :row-count (count ids)})]
        [dnd/wrap-dnd
         {:id               (str "conditions-dnd-" fsm-target-id)
          :items            ids
          :props-for-source {:class "translate-x-2"}
          :on-reorder       (fn [from to]
                              (>evt [::process/signal
                                     fsm-target-id
                                     [reorder-signal {:conditions (util/mmap from to)}]]))}
         (fn [{:keys [sorted] :as draggable-props}]
           (futil/for-frag-indexed [[index id] sorted]
             (let [*item-state (r/cursor *state [:items id])]
               ^{:key id}
               [condition-row (merge (util/mmap *item-state index id fsm-target-id group-id tr
                                                reordering? can-reorder? can-edit? can-delete?
                                                edit-signal delete-signal)
                                     (select-keys draggable-props
                                                  [:augment-target-props :augment-draggable-props]))])))]
        (->> (cond-> []
               (and can-reorder? (< 1 (count ids)))
               (conj [reorder-buttons (util/mmap tr *reordering)])

               (and (not reordering?) (seq other-controls))
               (into other-controls))
             (into [:div.flex.flex-row.space-x-2.h-10]))]])))

(defn can-edit-general-conditions?
  [verdict]
  ;; General conditions are always under the application phase so it's easier
  ;; to modify them during re-issue
  (-> verdict
      :domain/state
      condition-model/verdict-states-for-editing
      some?))

(defn general-conditions-view
  [{:keys [tr workspace verdict]}
   {verdict-section-id :db/id}]
  (let [title          :verdict/general-conditions
        group-id       (-> workspace :domain/authority-group :db/id)
        application-id (-> workspace workspace-model/workspace-application :db/id)
        editable?      (can-edit-general-conditions? verdict)]
    [:<>
     [condition-list
      (util/mmap {:fsm-target-id  application-id
                  ;; General conditions can be found under the verdict
                  :ids            (<sub [::verdict-condition-ids verdict-section-id])
                  :edit-signal    :process.signal/edit-general-condition
                  :delete-signal  :process.signal/delete-general-condition
                  :reorder-signal :process.signal/reorder-general-conditions}
                 tr group-id title editable?)
      (when editable?
        [add-general-condition-controls
         (util/mmap tr group-id application-id verdict-section-id)])]]))

(defn can-edit-permit-specific-conditions?
  [workspace verdict]
  ;; Permit-specific conditions are moved under the execution phase when the permit is issued.
  ;; If the permit is being re-issued, we're dealing with the application FSM
  ;; while the conditions are under the execution entity
  ;; -> very easy to query/signal the wrong FSM -> just forbid editing if re-issuing
  (and (= (workspace-model/workspace-target workspace)
          (workspace-model/workspace-application workspace))
       (-> verdict
           :domain/state
           condition-model/verdict-states-for-editing
           some?)))

(defn permit-specific-conditions-view
  [{:keys [tr workspace verdict]} _section]
  (let [title            :verdict/permit-specific-conditions
        application-id   (-> workspace workspace-model/workspace-application :db/id)
        current-phase-id (-> workspace workspace-model/workspace-target :db/id)
        editable?        (can-edit-permit-specific-conditions? workspace verdict)]
    [condition-list
     (util/mmap {:group-id       (-> workspace :domain/authority-group :db/id)
                 :fsm-target-id  application-id
                 ;; Current conditions are found under the current phase
                 :ids            (<sub [::phase-condition-ids current-phase-id])
                 :edit-signal    :process.signal/edit-application-condition
                 :delete-signal  :process.signal/delete-application-condition
                 :reorder-signal :process.signal/reorder-application-conditions}
                tr title editable?)]))
